import React, { useState } from 'react';
import styles from './SignalSearchPage.module.scss';
import { Heading, Text } from '../../components/Typography';
import { Button, AlertBanner } from '../../components/UI';
import CompanyResultsGrid from './components/CompanyResultsGrid';
import CompanyCard from './components/CompanyCard';
import { postSearch } from '../../api/signalSearchApi';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  loadMoreCompanies,
  resetResults,
  setAllExpanded,
  setCompanies,
} from '../../store/search/slice';
import { expandAllAndHydrate, toggleCompanyExpandedAndHydrate } from '../../store/search/thunks';
import DocumentViewer from '../../components/DocumentViewer/DocumentViewer';
import SignalFilters from './components/SignalFilters';
import { parseCalendarYearRange } from './utils/formatters';
import { trackEvent } from '../../services/tracking';

const SignalSearchPage: React.FC = () => {
  const filters = useAppSelector((s) => s.signalSearch.filters);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const companies = useAppSelector((s) => s.signalSearch.companies);
  const expandedCompanies = useAppSelector((s) => s.signalSearch.expandedCompanies);
  const visibleCompanyCount = useAppSelector((s) => s.signalSearch.visibleCompanyCount);

  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [expandAllLoading, setExpandAllLoading] = useState<boolean>(false);

  const isAllExpanded =
    companies.length > 0 && companies.every((c) => expandedCompanies[c.companyId]);

  const onSearch = async () => {
    dispatch(resetResults());
    setIsLoading(true);
    setError(null);
    setHasSearched(false);

    try {
      trackEvent('Signal Search Clicked', {
        query: filters.query,
        selectedCompanies: filters.selectedCompanies.map((c) => c.ticker),
        categories: filters.categories,
        types: filters.types,
        calendarYear: filters.calendarYear,
      });

      const { min, max } = parseCalendarYearRange(filters.calendarYear || '');
      const resp = await postSearch({
        query: filters.query,
        companyIds: filters.selectedCompanies.map((c) => c.id),
        categories: filters.categories,
        types: filters.types,
        minCalendarYear: min,
        maxCalendarYear: max,
      });
      dispatch(setCompanies(resp.companies));

      // If there's only one company, expand it by default
      if (resp.companies.length === 1) {
        const onlyCompanyId = resp.companies[0].companyId;
        await dispatch(toggleCompanyExpandedAndHydrate(onlyCompanyId));
      }

      setHasSearched(true);
    } catch (e) {
      setError('Search failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const expandAll = async () => {
    trackEvent('Signal Search Expand All Clicked', {
      companyCount: companies.length,
    });
    try {
      setExpandAllLoading(true);
      await dispatch(expandAllAndHydrate());
    } finally {
      setExpandAllLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.main}>
        <div className={styles.leftArea}>
          <div className={styles.header}>
            <Heading level={2}>Signal Search</Heading>
            <Text>
              Smartly search for companies that match the signals you're looking for across their
              documents.
            </Text>
          </div>
          <section className={styles.filtersSection}>
            <SignalFilters isLoading={isLoading} onSearch={onSearch} />
            {error && <AlertBanner type="error" message={error} />}
          </section>
        </div>
        <section className={styles.resultsColumn}>
          <div className={styles.resultsSection}>
            {hasSearched && (
              <div className={styles.resultsBar}>
                <Text>
                  {companies.length} {companies.length === 1 ? 'company' : 'companies'} matched
                </Text>
                {companies.length > 0 && (
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() =>
                      isAllExpanded ? dispatch(setAllExpanded({ expanded: false })) : expandAll()
                    }
                    disabled={(expandAllLoading || isLoading) && !isAllExpanded}
                    loading={!isAllExpanded && expandAllLoading}
                  >
                    {isAllExpanded ? 'Collapse all' : 'Expand all'}
                  </Button>
                )}
              </div>
            )}
            <CompanyResultsGrid>
              {companies.slice(0, visibleCompanyCount).map((c) => (
                <CompanyCard
                  key={c.companyId}
                  company={c}
                  expanded={!!expandedCompanies[c.companyId]}
                />
              ))}
            </CompanyResultsGrid>
            {!isLoading && hasSearched && companies.length === 0 && <Text>No results found.</Text>}
            {visibleCompanyCount < companies.length && (
              <div style={{ marginTop: 12 }}>
                <Button
                  variant="secondary"
                  onClick={() => dispatch(loadMoreCompanies())}
                  disabled={isLoading}
                >
                  Load 20 more
                </Button>
              </div>
            )}
          </div>
        </section>
        <section className={styles.viewerSection}>
          <div className={styles.documentContainer}>
            <DocumentViewer />
          </div>
        </section>
      </div>
    </div>
  );
};

export default SignalSearchPage;
